import * as React from 'react';
import { cn } from '../lib/utils';

export type ProgressBarProps = {
  /** Progress value between 0 and 100 */
  value: number;
  /** Color of the progress bar (CSS color value) */
  color?: string;
  /** Whether to show the percentage label on the right side */
  showLabel?: boolean;
  /** Height of the progress bar (Tailwind height class or custom CSS value) */
  height?: string;
  /** Additional CSS class for the container */
  className?: string;
};

/**
 * Progress bar component following Shelflix Admin design system.
 *
 * Features:
 * - Height: 16px
 * - Background: var(--color-neutral-950) with var(--color-neutral-900) border
 * - Border-radius: 50px (pill-shaped)
 * - Optional percentage label on the right
 */
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, color = 'var(--color-success)', showLabel = false, height = 'h-4', className }, ref) => {
    // Clamp value between 0 and 100
    const clampedValue = Math.max(0, Math.min(100, value));

    return (
      <div ref={ref} className={cn('flex w-full items-center gap-4', className)}>
        {/* Progress bar container */}
        <div
          className={cn(
            'relative flex-1 overflow-hidden rounded-[50px] border border-[var(--color-neutral-900)] bg-[var(--color-neutral-950)]',
            height
          )}
        >
          {/* Progress fill */}
          <div
            className={cn('absolute -top-px -left-px transition-all duration-300 ease-out', height)}
            style={{
              width: `${clampedValue > 0 ? clampedValue + 1 : 0}%`,
              backgroundColor: color,
            }}
          />
        </div>

        {/* Optional percentage label */}
        {showLabel && <span className="text-[var(--color-neutral-100)]">{Math.round(clampedValue)}%</span>}
      </div>
    );
  }
);
ProgressBar.displayName = 'ProgressBar';

export { ProgressBar };
